import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Modal } from '@/components/common/Modal';
import { SelectField, TextField } from '@/components/forms/Field';
import { useToast } from '@/components/feedback/ToastProvider';
import { useApiErrorHandler } from '@/hooks/useApiErrorHandler';
import { leaveBalancesApi, leaveRequestsApi, leaveTypesApi } from '@/services/api/endpoints/hr';
import { queryKeys } from '@/services/api/queryKeys';
import type { LeaveRequest } from '@/types/hr';
import { formatDate } from '@/utils/format';
import { Days, leaveYearOf, today } from './shared';
import { EmployeePicker, type PickedEmployee } from './EmployeePicker';

/**
 * Filing leave.
 *
 * <p>Two modes, one form. Self-service resolves the employee from the caller's
 * own account and enforces the notice period; HR filing on somebody's behalf
 * names the employee and skips notice, because somebody off sick on Monday is
 * entered on Tuesday and refusing that would make the system unusable for the
 * case it most needs to record.
 *
 * <p>The days are not counted here. The server works out what the request costs
 * against the school's working week and holiday calendar, and the response says
 * which days it did not charge — so a fortnight that costs ten days is explained
 * rather than merely stated.
 */
export function LeaveRequestDialog({
  mode,
  onClose,
  onFiled,
}: {
  mode: 'SELF' | 'ON_BEHALF';
  onClose: () => void;
  onFiled: (request: LeaveRequest) => void;
}) {
  const toast = useToast();
  const handleApiError = useApiErrorHandler();

  const [employee, setEmployee] = useState<PickedEmployee | null>(null);
  const [leaveTypeId, setLeaveTypeId] = useState('');
  const [startDate, setStartDate] = useState(today());
  const [endDate, setEndDate] = useState(today());
  const [halfDay, setHalfDay] = useState(false);
  const [reason, setReason] = useState('');
  const [filed, setFiled] = useState<LeaveRequest | null>(null);

  const types = useQuery({
    queryKey: queryKeys.leaveTypes.list(true),
    queryFn: () => leaveTypesApi.list(true),
  });

  /*
   * The caller's own balances, shown against the chosen type. Only for
   * self-service: on behalf of somebody else the balance belongs to them, and
   * fetching it would need a second permission this dialog should not assume.
   */
  const balances = useQuery({
    queryKey: queryKeys.leaveBalances.mine(leaveYearOf(startDate)),
    queryFn: () => leaveBalancesApi.mine(leaveYearOf(startDate)),
    enabled: mode === 'SELF',
  });

  const selectedType = useMemo(
    () => (types.data ?? []).find((type) => type.id === leaveTypeId),
    [types.data, leaveTypeId],
  );

  const balance = useMemo(
    () => (balances.data ?? []).find((row) => row.leaveTypeId === leaveTypeId),
    [balances.data, leaveTypeId],
  );

  const file = useMutation({
    mutationFn: () => {
      const payload = {
        leaveTypeId,
        startDate,
        endDate: halfDay ? startDate : endDate,
        halfDay,
        reason: reason.trim(),
        ...(mode === 'ON_BEHALF' && employee !== null ? { employeeId: employee.id } : {}),
      };
      return mode === 'SELF'
        ? leaveRequestsApi.submitForSelf(payload)
        : leaveRequestsApi.submitFor(payload);
    },
    onSuccess: (request) => {
      toast.success(`${request.requestNumber} submitted`);
      // Held rather than closed: the response carries the days that were not
      // charged, which is the one moment the user can be told why.
      setFiled(request);
    },
    onError: (error) => handleApiError(error),
  });

  if (filed !== null) {
    return (
      <Modal open onClose={() => onFiled(filed)} title={`${filed.requestNumber} submitted`}>
        <dl className="text-sm">
          <Row label="Leave type" value={filed.leaveTypeName ?? '—'} />
          <Row
            label="Dates"
            value={
              filed.startDate === filed.endDate
                ? formatDate(filed.startDate)
                : `${formatDate(filed.startDate)} → ${formatDate(filed.endDate)}`
            }
          />
          <Row label="Charged" value={<Days value={filed.days} />} />
          {filed.balanceRemaining !== undefined && (
            <Row label="Remaining after this" value={<Days value={filed.balanceRemaining} />} />
          )}
        </dl>

        {filed.skippedDates !== undefined && filed.skippedDates.length > 0 && (
          <div className="mt-3 flex items-start gap-2 border border-line bg-surface p-3">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" />
            <p className="text-[13px] text-muted">
              {filed.skippedDates.length} day
              {filed.skippedDates.length === 1 ? ' was' : 's were'} not charged — weekends and
              holidays:{' '}
              <span className="text-ink">
                {filed.skippedDates.map((date) => formatDate(date)).join(', ')}
              </span>
            </p>
          </div>
        )}

        <div className="mt-4 flex justify-end">
          <Button onClick={() => onFiled(filed)}>Done</Button>
        </div>
      </Modal>
    );
  }

  const disabled =
    leaveTypeId === '' ||
    reason.trim() === '' ||
    (mode === 'ON_BEHALF' && employee === null) ||
    file.isPending;

  return (
    <Modal open onClose={onClose} title={mode === 'SELF' ? 'Request leave' : 'File leave on behalf'}>
      <form
        className="space-y-3"
        onSubmit={(event) => {
          event.preventDefault();
          file.mutate();
        }}
      >
        {mode === 'ON_BEHALF' && <EmployeePicker value={employee} onChange={setEmployee} />}

        <SelectField
          label="Leave type"
          required
          value={leaveTypeId}
          onChange={(event) => setLeaveTypeId(event.target.value)}
        >
          <option value="">Choose…</option>
          {(types.data ?? []).map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
              {type.paid ? '' : ' (unpaid)'}
            </option>
          ))}
        </SelectField>

        {selectedType !== undefined && (
          <div className="border border-line bg-surface p-3 text-[13px] text-muted">
            {balance !== undefined && (
              <p className="text-ink">
                <Days value={balance.remainingDays} /> day
                {balance.remainingDays === 1 ? '' : 's'} left of {selectedType.name} this year
                {balance.pendingDays > 0 && (
                  <span className="text-muted">
                    {' '}
                    (<Days value={balance.pendingDays} /> already pending)
                  </span>
                )}
              </p>
            )}
            <ul className="mt-1 space-y-0.5">
              {mode === 'SELF' && selectedType.minNoticeDays > 0 && (
                <li>Needs {selectedType.minNoticeDays} days notice.</li>
              )}
              {selectedType.requiresDocument && <li>A supporting document is required.</li>}
              {selectedType.maxConsecutiveDays !== undefined && (
                <li>At most {selectedType.maxConsecutiveDays} days at a time.</li>
              )}
              {selectedType.requiresHrApproval ? (
                <li>Goes to your manager, then to HR.</li>
              ) : (
                <li>Your manager decides; no HR step.</li>
              )}
              {selectedType.countsNonWorkingDays ? (
                <li>Weekends and holidays inside the range are charged.</li>
              ) : (
                <li>Weekends and holidays are not charged.</li>
              )}
            </ul>
          </div>
        )}

        <label className="flex items-center gap-2 text-sm text-ink">
          <input
            type="checkbox"
            checked={halfDay}
            onChange={(event) => setHalfDay(event.target.checked)}
            className="h-4 w-4 border-line"
          />
          Half day
        </label>

        <div className="grid gap-3 sm:grid-cols-2">
          <TextField
            label="First day"
            type="date"
            required
            value={startDate}
            onChange={(event) => {
              setStartDate(event.target.value);
              if (endDate < event.target.value) {
                setEndDate(event.target.value);
              }
            }}
          />
          {!halfDay && (
            <TextField
              label="Last day"
              type="date"
              required
              min={startDate}
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
          )}
        </div>

        <TextField
          label="Reason"
          required
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Why you need the time"
        />

        {selectedType?.requiresDocument === true && (
          <p className="text-[13px] text-[color:var(--sms-warning)]">
            This type needs a supporting document. Submit the request first, then attach the
            document from {mode === 'SELF' ? 'My requests' : 'the request'} while it awaits a
            decision — it cannot be approved without one.
          </p>
        )}

        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={disabled}>
            {file.isPending ? 'Submitting…' : 'Submit request'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between border-b border-line py-2 last:border-0">
      <dt className="text-xs uppercase tracking-[0.04em] text-muted">{label}</dt>
      <dd className="text-sm text-ink">{value}</dd>
    </div>
  );
}
